import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sheet, RefreshCw, ExternalLink, Unlink, CheckCircle2 } from "lucide-react";

export const GoogleSheetsSync = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchStatus = async () => {
    try { const res = await axios.get(`${API}/sheets/status`, { headers }); setStatus(res.data); }
    catch { setStatus({ connected: false }); }
  };

  useEffect(() => {
    fetchStatus();
    const p = new URLSearchParams(window.location.search);
    if (p.get("sheets") === "connected") { toast.success("Google Sheets connecté ✅"); window.history.replaceState({}, "", "/parametres"); }
    if (p.get("sheets") === "error") { toast.error("Échec de la connexion Google"); window.history.replaceState({}, "", "/parametres"); }
  }, []); // eslint-disable-line

  const connect = () => { window.location.href = `${API}/oauth/sheets/login?auth=${token}`; };

  const sync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/sheets/sync`, {}, { headers, timeout: 120000 });
      const total = Object.values(res.data.lignes || {}).reduce((a, b) => a + b, 0);
      toast.success(`Synchronisé : ${res.data.onglets?.length || 0} onglets, ${total} lignes`);
      fetchStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Échec de la synchronisation"); }
    finally { setSyncing(false); }
  };

  const toggleAuto = async (val) => {
    setStatus({ ...status, auto_sync: val });
    try { await axios.put(`${API}/sheets/config`, { auto_sync: val }, { headers }); }
    catch { toast.error("Erreur"); }
  };

  const disconnect = async () => {
    if (!window.confirm("Déconnecter Google Sheets ? (le tableur existant n'est pas supprimé)")) return;
    try { await axios.post(`${API}/sheets/disconnect`, {}, { headers }); toast.success("Déconnecté"); fetchStatus(); }
    catch { toast.error("Erreur"); }
  };

  if (!status) return null;

  return (
    <Card data-testid="sheets-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Sheet className="w-5 h-5 text-emerald-600" />
          Sauvegarde Google Sheets
          {status.connected && <Badge className="bg-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" />Connecté</Badge>}
        </CardTitle>
        <CardDescription>
          Copie complète de l'app (livraisons, clients, ventes, stock, charges, tournées, véhicules…) dans un Google Sheet avec graphiques. Sync automatique 1x/jour.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.connected ? (
          <Button onClick={connect} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sheets-connect-btn">
            <Sheet className="w-4 h-4 mr-2" />Connecter Google Sheets
          </Button>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={sync} disabled={syncing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="sheets-sync-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Synchronisation…" : "Synchroniser maintenant"}
              </Button>
              {status.spreadsheet_url && (
                <a href={status.spreadsheet_url} target="_blank" rel="noopener noreferrer" data-testid="sheets-open-link">
                  <Button variant="outline"><ExternalLink className="w-4 h-4 mr-2" />Ouvrir le tableur</Button>
                </a>
              )}
              <Button variant="ghost" className="text-red-500" onClick={disconnect} data-testid="sheets-disconnect-btn">
                <Unlink className="w-4 h-4 mr-1" />Déconnecter
              </Button>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Switch checked={status.auto_sync !== false} onCheckedChange={toggleAuto} data-testid="sheets-auto-switch" />
                <Label>Sauvegarde automatique quotidienne</Label>
              </div>
              {status.last_sync && <span className="text-slate-500">Dernière sync : {new Date(status.last_sync).toLocaleString("fr-BE")}</span>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default GoogleSheetsSync;
